import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import type { Team } from '../../models';

interface TeamEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  team: Partial<Team>;
  onTeamChange: (team: Partial<Team>) => void;
  isNew?: boolean;
  loading?: boolean;
}

const TeamEditDialog: React.FC<TeamEditDialogProps> = ({
  open,
  onClose,
  onSave,
  team,
  onTeamChange,
  isNew = false,
  loading = false,
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    onTeamChange({
      ...team,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === '' ? null : Number(value);
    onTeamChange({
      ...team,
      [name]: numValue,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isNew ? 'Add New Team' : 'Edit Team'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          {/* Basic Information */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="name"
                  label="Name"
                  fullWidth
                  value={team.name || ''}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="abbreviation"
                  label="Abbreviation"
                  fullWidth
                  value={team.abbreviation || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="long_name"
                  label="Long Name"
                  fullWidth
                  value={team.long_name || ''}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Fantasy Platform Names */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Fantasy Platform Names
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="d_name"
                  label="Dream11 Name"
                  fullWidth
                  value={team.d_name || ''}
                  onChange={handleInputChange}
                  helperText="Team name on Dream11 platform"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="c_name"
                  label="Circle11 Name"
                  fullWidth
                  value={team.c_name || ''}
                  onChange={handleInputChange}
                  helperText="Team name on Circle11 platform"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="mt_name"
                  label="MyTeam11 Name"
                  fullWidth
                  value={team.mt_name || ''}
                  onChange={handleInputChange}
                  helperText="Team name on MyTeam11 platform"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="f_name"
                  label="Fab11 Name"
                  fullWidth
                  value={team.f_name || ''}
                  onChange={handleInputChange}
                  helperText="Team name on Fab11 platform"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Fantasy Platform IDs */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Fantasy Platform IDs
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="d_id"
                  label="Dream11 ID"
                  type="number"
                  fullWidth
                  value={team.d_id || ''}
                  onChange={handleNumberInputChange}
                  helperText="Team ID on Dream11 platform"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="c_id"
                  label="Circle11 ID"
                  type="number"
                  fullWidth
                  value={team.c_id || ''}
                  onChange={handleNumberInputChange}
                  helperText="Team ID on Circle11 platform"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="mt_id"
                  label="MyTeam11 ID"
                  type="number"
                  fullWidth
                  value={team.mt_id || ''}
                  onChange={handleNumberInputChange}
                  helperText="Team ID on MyTeam11 platform"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="f_id"
                  label="Fab11 ID"
                  type="number"
                  fullWidth
                  value={team.f_id || ''}
                  onChange={handleNumberInputChange}
                  helperText="Team ID on Fab11 platform"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="e_id"
                  label="ESPN ID"
                  type="number"
                  fullWidth
                  value={team.e_id || ''}
                  onChange={handleNumberInputChange}
                  helperText="Team ID on ESPN platform"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="e_o_id"
                  label="ESPN O ID"
                  type="number"
                  fullWidth
                  value={team.e_o_id || ''}
                  onChange={handleNumberInputChange}
                  helperText="Team ID on ESPN O platform"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Team Status */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Team Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_international"
                      checked={team.is_international || false}
                      onChange={handleInputChange}
                    />
                  }
                  label="International Team"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_test_playing"
                      checked={team.is_test_playing || false}
                      onChange={handleInputChange}
                    />
                  }
                  label="Test Playing"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_odi_playing"
                      checked={team.is_odi_playing || false}
                      onChange={handleInputChange}
                    />
                  }
                  label="ODI Playing"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_active"
                      checked={team.is_active || false}
                      onChange={handleInputChange}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={onSave} 
          variant="contained" 
          color="primary"
          disabled={loading || !team.name?.trim()}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamEditDialog; 